import {
  Activity,
  ClipboardCheck,
  Stethoscope,
  HeartPulse,
  Users,
  Thermometer,
  ShieldCheck,
  Plus,
  Eye,
  CreditCard,
  Zap,
  ChevronRight,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAdminStats } from "../../hooks/useAdminStats";

const DashboardHome = () => {
  const { data, isLoading, isRefetching: refreshing, refetch } = useAdminStats();

  if (isLoading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-pulse flex items-center justify-center">
            <Activity className="w-10 h-10 text-white" />
          </div>
          <p className="mt-6 text-lg text-gray-700">Chargement des données réelles...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-slate-200 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tableau de bord</h1>
            <p className="text-sm text-gray-600 mt-1">Données 100 % réelles • Mise à jour automatique</p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={refreshing}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="px-6 py-8 space-y-8">
        {/* Cartes principales */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link to="/admin/dashboard/patient" className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Activity className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600">Consultations aujourd'hui</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.todayConsultations}</p>
          </Link>

          <Link to="/admin/dashboard/record" className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <ClipboardCheck className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600">Dossiers médicaux</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.totalRecords}</p>
          </Link>

          <Link to="/admin/dashboard/record" className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <ClipboardCheck className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600">Nouveaux aujourd'hui</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.todayRecords}</p>
          </Link>

          <Link to="/admin/dashboard/account/doctor" className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Stethoscope className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600">Médecins actifs</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.totalDoctors}</p>
          </Link>
        </div>

        {/* Cartes secondaires */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Link to="/admin/dashboard/constante" className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border-l-4 ${data.pendingConstantes > 0 ? "border-red-500 bg-red-50/50" : "border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <Thermometer className="w-7 h-7 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Constantes en attente</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{data.pendingConstantes}</p>
                </div>
              </div>
              {data.pendingConstantes > 0 && <AlertCircle className="w-6 h-6 text-red-600" />}
            </div>
          </Link>

          <Link to="/admin/dashboard/account/patient" className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-xl">
                <Users className="w-7 h-7 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Patients total</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{data.totalPatients}</p>
              </div>
            </div>
          </Link>

          <Link to="/admin/dashboard/account/admin" className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-100 rounded-xl">
                <ShieldCheck className="w-7 h-7 text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Admins</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{data.totalAdmins}</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Accès rapide */}
        <div className="bg-white rounded-2xl p-6 shadow-md border border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">Accès rapide</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/admin/dashboard/patient/new" className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-all group">
              <div className="p-3 bg-blue-200 rounded-xl group-hover:scale-110 transition-transform">
                <Plus className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Nouvelle consultation</p>
                <p className="text-xs text-gray-600">Patient + constantes</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </Link>

            <Link to="/admin/dashboard/constante/new" className="flex items-center gap-4 p-4 rounded-xl bg-red-50 hover:bg-red-100 transition-all group">
              <div className="p-3 bg-red-200 rounded-xl group-hover:scale-110 transition-transform">
                <HeartPulse className="w-6 h-6 text-red-700" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Signes vitaux</p>
                <p className="text-xs text-gray-600">Enregistrer constantes</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </Link>

            <Link to="/admin/dashboard/record" className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-all group">
              <div className="p-3 bg-emerald-200 rounded-xl group-hover:scale-110 transition-transform">
                <Eye className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Dossiers médicaux</p>
                <p className="text-xs text-gray-600">Consulter historique</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </Link>

            <Link to="/admin/dashboard/account/doctor/new" className="flex items-center gap-4 p-4 rounded-xl bg-purple-50 hover:bg-purple-100 transition-all group">
              <div className="p-3 bg-purple-200 rounded-xl group-hover:scale-110 transition-transform">
                <Stethoscope className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Ajouter docteur</p>
                <p className="text-xs text-gray-600">Nouveau compte docteur</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </Link>
          </div>
        </div>

        {/* Impayés */}
        {data.totalUnpaid > 0 && (
          <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl p-1 shadow-2xl">
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="p-4 bg-red-100 rounded-full">
                    <CreditCard className="w-10 h-10 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Impayés totaux</h3>
                    <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-2">
                      {data.totalUnpaid.toLocaleString()} MRU
                    </p>
                  </div>
                </div>
                <Link
                  to="/admin/dashboard/patient"
                  className="px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-all"
                >
                  Gérer les impayés
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;